// The right-hand column, mirroring WeatherSummary on the left: same 25% width,
// same glass card, same blue glow. Top to bottom it holds
//
//   1. Today's Pick   — the rotating, weather-driven dish recommendation
//   2. Weather Alert  — the most recent alert for this city, or an all-clear
//   3. a station footer — which station the reading on screen comes from
//
// Replaces InfoOverlay, which floated these as absolutely-positioned elements
// over the top-right corner. That corner is where the food carousel's cards
// live, so the alert had to be squeezed into the thin band above them and got
// clipped. As a real flex column between the weather panel and the carousel,
// this cannot overlap a card at all — the carousel is a sibling that now owns
// only the middle 50% of the screen.

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLatestAlert } from '../hooks/useLatestAlert';
import { LiveAlertPanel } from './LiveAlertPanel';

const FONT_STACK = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
// How long each recommendation line stays before rotating to the next.
const LINE_ROTATE_MS = 12 * 1000;
// How many of the ranked dishes the line cycles through.
const ROTATING_LINES = 3;

const SECTION_LABEL = {
  color: '#c2c2c2',
  fontSize: 13,
  letterSpacing: 1,
  textTransform: 'uppercase',
  display: 'block',
  marginBottom: 14,
  fontFamily: FONT_STACK
};

// Short, glanceable pairing lines, taken straight from the ranked dishes so the
// line and the carousel can never contradict each other.
function buildLines(foodRecommendations) {
  const lines = (foodRecommendations || [])
    .slice(0, ROTATING_LINES)
    .map((food) => food.headline)
    .filter(Boolean);

  return lines.length ? lines : ['Perfect weather for a feast at TLT'];
}

export const InfoPanel = ({ stations, foodRecommendations }) => {
  const activeAlert = useLatestAlert();
  const [lineIndex, setLineIndex] = useState(0);

  const lines = buildLines(foodRecommendations);
  const line = lines[lineIndex % lines.length];

  useEffect(() => {
    const timer = setInterval(() => {
      setLineIndex((prev) => prev + 1);
    }, LINE_ROTATE_MS);
    return () => clearInterval(timer);
  }, []);

  const servingStation = (stations || []).find((station) => station.isServing);
  // Stations arrive ordered closest-first, so serving anything but the first
  // means the primary station is down and the fallback is on duty.
  const servingFallback = servingStation && stations[0] && servingStation.id !== stations[0].id;

  return (
    <div
      style={{
        width: '25%',
        height: '100vh',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          position: 'relative',
          height: '100%',
          boxSizing: 'border-box',
          background: 'rgba(15, 15, 15, 0.6)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '24px',
          padding: '40px 30px',
          boxShadow:
            '0 0 10px rgba(70,150,255,0.4), 0 0 40px rgba(50,120,220,0.25), 0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* 1. Rotating dish recommendation */}
        <div style={{ marginBottom: 32 }}>
          <span style={SECTION_LABEL}>Today&rsquo;s Pick</span>
          <AnimatePresence mode="wait">
            <motion.div
              key={`line-${lineIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, filter: 'blur(4px)' }}
              transition={{ duration: 0.8 }}
              style={{
                color: '#ffffff',
                fontSize: 22,
                fontWeight: 500,
                letterSpacing: 0.2,
                lineHeight: 1.45,
                textShadow: '0 2px 18px rgba(0,0,0,0.55)',
                fontFamily: FONT_STACK
              }}
            >
              {line.split(' ').map((word, index) => (
                <motion.span
                  key={index}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.12, duration: 0.5, ease: 'easeOut' }}
                  style={{ display: 'inline-block', marginRight: 7 }}
                >
                  {word}
                </motion.span>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        <div style={{
          height: 1,
          background: 'rgba(255,255,255,0.08)',
          marginBottom: 28
        }} />

        {/* 2. Most recent weather alert (or all-clear) */}
        <div>
          <span style={SECTION_LABEL}>Weather Alert</span>
          <LiveAlertPanel alert={activeAlert} />
        </div>

        {/* 3. Which station the nowcast on screen comes from. Pushed to the
            bottom of the card — it used to be a floating chip in this corner. */}
        {servingStation && (
          <div style={{
            marginTop: 'auto',
            paddingTop: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'rgba(255,255,255,0.5)',
            fontSize: 12,
            letterSpacing: 0.2,
            fontFamily: FONT_STACK
          }}>
            <span style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              flexShrink: 0,
              background: servingFallback ? '#f59e0b' : '#22c55e',
              boxShadow: `0 0 8px ${servingFallback ? 'rgba(245,158,11,0.8)' : 'rgba(34,197,94,0.8)'}`
            }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {`Live · ${servingStation.name} · ${servingStation.distanceKm} km`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
